// Annotation — inline comment / tracked-change anchored to a text
// range inside an article block. Mirrors `@viquard/editor`'s `Comment`
// shape (`packages/editor/src/plugins/comments/types.ts`) so the editor
// can `initialComments: Annotation[]` directly from a list-fetch and
// `onCommentsChange` deltas map 1-to-1 to single-resource POST/PATCH/
// DELETE calls.
//
// Field-name divergences from the editor's Comment:
//   • `id` is a server UUID (not a client-generated nanoid)
//   • `createdAt` is ISO-8601 (not a JS millisecond timestamp); the
//     editor's `sanitizeComment` accepts both, so the SPA converts on
//     hydration. We document the wire format as ISO for HTTP-API
//     consistency with every other DTO.
//   • `author` is omitted in favour of structured `authorId` +
//     `authorDisplayName` (privacy: never expose raw author strings).
import { z } from 'zod';
import { ProfileAvatarDesign } from '../members/avatar.js';
// ---------------------------------------------------------------------------
// Closed enums — keep in sync with CHECK constraints in migration 0006
// AND with the consts in `packages/db/src/schema/content/annotations.ts`.
// ---------------------------------------------------------------------------
export const AnnotationType = z.enum(['comment', 'insertion', 'deletion']);
export const AnnotationScope = z.enum(['public', 'private', 'group']);
// ---------------------------------------------------------------------------
// Annotation — canonical wire shape.
// ---------------------------------------------------------------------------
const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}){1,2}(?:[0-9a-fA-F]{2})?$/;
export const Annotation = z.object({
    id: z.string().uuid(),
    articleId: z.string().uuid(),
    authorId: z.string().uuid(),
    /** Joined from `users.display_name` at the route layer for client
     *  rendering. Null in viewer-less contexts (cron jobs, raw activity
     *  payloads). */
    authorDisplayName: z.string().nullable(),
    authorAvatarDesign: ProfileAvatarDesign.nullable().optional(),
    // ---- editor anchor ----
    anchorBlockId: z.string().min(1).max(128),
    anchorOffset: z.number().int().min(0),
    anchorLength: z.number().int().min(0),
    // ---- content ----
    content: z.string().max(4000),
    color: z.string().regex(HEX_COLOR_PATTERN),
    // ---- type / tracked changes ----
    type: AnnotationType,
    /** Original text for type='deletion'. Null otherwise. */
    deletedContent: z.string().nullable(),
    // ---- resolution ----
    resolved: z.boolean(),
    resolvedBy: z.string().uuid().nullable(),
    resolvedAt: z.string().datetime({ offset: true }).nullable(),
    // ---- visibility ----
    scope: AnnotationScope,
    /** Group attachments live in `resource_groups (kind='annotation')`.
     *  Denormalised onto the DTO so clients render visibility chips
     *  without a second round-trip. */
    groupIds: z.array(z.string().uuid()),
    // ---- viewer-relative annotation ----
    /** Whether the requesting viewer can edit (annotation author OR
     *  article author OR manager+). Null in viewer-less contexts. */
    viewerCanWrite: z.boolean().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=annotation.js.map